import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { X, Send, ThumbsUp, ThumbsDown, Sparkles, AlertCircle, Popcorn, Clock, Pencil, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  feedback?: 'positive' | 'negative' | null;
  isStreaming?: boolean;
}

interface ChatStatus {
  remaining: number;
  limit: number;
  isPro: boolean;
  currentSession: string | null;
}

export function ChatBox() {
  const { user, session } = useAuth();
  const { isOpen, pendingQuestion, openChat, closeChat, clearPendingQuestion } = useChat();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [intro, setIntro] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [loadedSessionId, setLoadedSessionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState<Array<{id: string; title: string | null; started_at: string; last_message_at: string; message_count: number; is_active: boolean}>>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [sessionsHydrated, setSessionsHydrated] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sessionMessagesCacheRef = useRef<Record<string, ChatMessage[]>>({});
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const historyContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const didBootstrapScroll = useRef(false);
  const lastScrollTop = useRef(0);
  const savedScrollTop = useRef<number | null>(null);
  const shouldRestoreOnReopen = useRef(false);
  const scrollLockActive = useRef(false);
  const savedScrollStyles = useRef<{ overflow: string } | null>(null);
  const wheelUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep per-session message cache updated (non-empty guard prevents hydration clear pollution)
  useEffect(() => {
    if (!sessionId || messages.length === 0) return;
    sessionMessagesCacheRef.current[sessionId] = messages;
  }, [sessionId, messages]);

  // Load chat history when session exists and chat opens
  useEffect(() => {
    if (!(isOpen && sessionId && session?.access_token && !isLoading)) return;

    // Capture for closure (TS narrowing doesn't flow into async)
    const sid = sessionId;

    // Skip fetch when session is already tracked as loaded
    const hasCachedCurrentSession = historyLoaded && loadedSessionId === sid;
    if (hasCachedCurrentSession) return;

    // Serve from in-memory cache if available (no network, no skeleton)
    const hasCached = Object.prototype.hasOwnProperty.call(sessionMessagesCacheRef.current, sid);
    if (hasCached) {
      setMessages(sessionMessagesCacheRef.current[sid]);
      setLoadedSessionId(sid);
      setHistoryLoaded(true);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    // Clear stale visible data immediately
    setMessages([]);

    async function loadHistory() {
      try {
        const response = await fetch(`${API_BASE_URL}/chat/history?session_id=${sid}`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
          signal: controller.signal,
        });

        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();
          if (!cancelled) {
            if (data.messages?.length > 0) {
              const mapped = data.messages.map((m: { id: string; role: string; content: string }) => ({
                id: m.id,
                role: m.role as 'user' | 'assistant',
                content: m.content,
                feedback: null,
                isStreaming: false
              }));
              sessionMessagesCacheRef.current[sid] = mapped;
              setMessages(mapped);
            } else {
              sessionMessagesCacheRef.current[sid] = [];
              setMessages([]);
            }
          }
        }
      } catch (err) {
        if (!cancelled && !(err instanceof DOMException && err.name === 'AbortError')) {
          console.error('Failed to load chat history:', err);
        }
      } finally {
        if (!cancelled) {
          setLoadedSessionId(sid);
          setHistoryLoaded(true);
        }
      }
    }

    loadHistory();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isOpen, sessionId, session?.access_token, isLoading, historyLoaded, loadedSessionId]);

  // Reset sessions cache when auth identity changes
  useEffect(() => {
    setSessions([]);
    setSessionsHydrated(false);
    setSessionsError(null);
  }, [user?.id]);

  // Ref to track sessionId for fetchSessions without causing re-creation
  const sessionIdRef = useRef<string | null>(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Fetch chat sessions list
  const fetchSessions = useCallback(async () => {
    if (!session?.access_token) return;
    setSessionsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/chat/sessions`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
        setSessionsError(null);
        setSessionsHydrated(true);
        const latestSessionId = data.sessions?.[0]?.id ?? null;
        if (latestSessionId !== sessionIdRef.current) {
          setSessionId(latestSessionId);
          setMessages([]);
          setHistoryLoaded(false);
          setLoadedSessionId(null);
          didBootstrapScroll.current = false;
          userScrolledUp.current = false;
        }
      } else {
        if (!sessionsHydrated) {
          setSessionsError('Unable to load past conversations');
        }
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      if (!sessionsHydrated) {
        setSessionsError('Unable to load past conversations');
      }
    } finally {
      setSessionsLoading(false);
    }
  }, [session?.access_token, sessionsHydrated]);

  // Quick chat follows the latest backend thread (shared model with /filmply page).
  useEffect(() => {
    if (!isOpen) return;
    if (!session?.access_token) return;
    fetchSessions();
  }, [isOpen, session?.access_token, fetchSessions]);

  // Rename a chat session
  const renameSession = useCallback(async (id: string, newTitle: string) => {
    if (!session?.access_token) return;
    const trimmed = newTitle.trim();
    if (!trimmed) {
      setRenameError('Title cannot be empty');
      return;
    }
    if (trimmed.length > 80) {
      setRenameError('Title must be 80 characters or fewer');
      return;
    }
    setRenameSaving(true);
    setRenameError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/chat/sessions/${id}/title`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ title: trimmed }),
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(prev => prev.map(s => s.id === id ? { ...s, title: data.session.title } : s));
        setEditingSessionId(null);
      } else {
        const data = await response.json();
        setRenameError(data.error || 'Failed to rename');
      }
    } catch {
      setRenameError('Failed to rename');
    } finally {
      setRenameSaving(false);
    }
  }, [session?.access_token]);

  const startEditing = (s: { id: string; title: string | null }) => {
    setEditingSessionId(s.id);
    setEditingTitle(s.title || '');
    setRenameError(null);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
    setEditingTitle('');
    setRenameError(null);
  };

  // Relative time helper
  const getRelativeTime = (dateStr: string) => {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diff = now - then;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  };

  // Initialize scroll tracking when chat opens
  useEffect(() => {
    if (!isOpen) return;

    const el = messagesContainerRef.current;
    if (!el) return;

    // Initialize lastScrollTop for delta detection
    lastScrollTop.current = el.scrollTop;

    // Initialize savedScrollTop only if null (preserves existing saved position on reopen)
    if (savedScrollTop.current === null) {
      savedScrollTop.current = el.scrollTop;
    }

    // Cleanup: mark that we should restore on next open
    return () => {
      shouldRestoreOnReopen.current = true;
    };
  }, [isOpen]);

  // Fetch intro message
  useEffect(() => {
    async function fetchIntro() {
      try {
        const response = await fetch(`${API_BASE_URL}/chat/intro`);
        const data = await response.json();
        if (data.intro) {
          setIntro(data.intro);
        }
      } catch (err) {
        console.error('Failed to fetch intro:', err);
      }
    }
    fetchIntro();
  }, []);

  // Fetch chat status when logged in
  const fetchStatus = useCallback(async () => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/chat/status`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  }, [session?.access_token]);

  useEffect(() => {
    if (user && isOpen) {
      fetchStatus();
    }
  }, [user, isOpen, fetchStatus]);

  // Overflow-only transient lock helpers (no paddingRight, no scrollbarGutter)
  const lockPageScrollForChat = useCallback(() => {
    if (scrollLockActive.current) return;
    const scrollEl = (document.scrollingElement as HTMLElement) || document.documentElement;
    savedScrollStyles.current = { overflow: scrollEl.style.overflow };
    scrollEl.style.overflow = 'hidden';
    scrollLockActive.current = true;
  }, []);

  const unlockPageScrollForChat = useCallback(() => {
    if (!scrollLockActive.current) return;
    const scrollEl = (document.scrollingElement as HTMLElement) || document.documentElement;
    scrollEl.style.overflow = savedScrollStyles.current?.overflow ?? '';
    savedScrollStyles.current = null;
    scrollLockActive.current = false;
  }, []);

  // Transient burst lock: active only during non-cancelable wheel bursts (250ms debounce)
  const armWheelSessionLock = useCallback(() => {
    lockPageScrollForChat();
    if (wheelUnlockTimerRef.current) clearTimeout(wheelUnlockTimerRef.current);
    wheelUnlockTimerRef.current = setTimeout(() => {
      unlockPageScrollForChat();
      wheelUnlockTimerRef.current = null;
    }, 250);
  }, [lockPageScrollForChat, unlockPageScrollForChat]);

  // Full manual wheel takeover — all scrolling inside chat is manually proxied
  const handleChatWheel = useCallback((e: WheelEvent) => {
    // Gate: only handle if pointer is inside chat window bounds
    const chatEl = chatWindowRef.current;
    if (!chatEl) return;
    const r = chatEl.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;

    // Consume event: block native scroll (cancelable) and stop propagation (always)
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();

    const activeContainer = showHistory
      ? historyContainerRef.current
      : messagesContainerRef.current;

    // If container ref not mounted yet, still consume + arm lock for non-cancelable, then return
    if (!activeContainer) {
      if (!e.cancelable) armWheelSessionLock();
      return;
    }

    // Normalize deltaY for line/page wheel modes
    let deltaY = e.deltaY;
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) deltaY *= 16;
    if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) deltaY *= activeContainer.clientHeight;

    // Manual scroll proxy — browser never handles wheel inside chat
    activeContainer.scrollTop += deltaY;

    // Non-cancelable burst: transient overflow lock to catch momentum frames
    if (!e.cancelable) armWheelSessionLock();
  }, [showHistory, armWheelSessionLock]);

  // Attach window-level non-passive capture wheel listener when chat is open
  useLayoutEffect(() => {
    if (!isOpen) return;
    window.addEventListener('wheel', handleChatWheel, { capture: true, passive: false });
    return () => {
      window.removeEventListener('wheel', handleChatWheel, { capture: true });
    };
  }, [isOpen, handleChatWheel]);

  // Ensure burst lock is released when chat closes or unmounts
  useEffect(() => {
    if (!isOpen) {
      if (wheelUnlockTimerRef.current) { clearTimeout(wheelUnlockTimerRef.current); wheelUnlockTimerRef.current = null; }
      unlockPageScrollForChat();
    }
    return () => {
      if (wheelUnlockTimerRef.current) { clearTimeout(wheelUnlockTimerRef.current); wheelUnlockTimerRef.current = null; }
      unlockPageScrollForChat();
    };
  }, [isOpen, unlockPageScrollForChat]);

  // Track when user scrolls up - with scroll direction detection
  // Save scroll position only when it reflects user intent (prevents auto-scroll overwriting it)
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const currentScrollTop = container.scrollTop;
    const scrollDelta = currentScrollTop - lastScrollTop.current;
    lastScrollTop.current = currentScrollTop;

    const isNearBottom = container.scrollHeight - currentScrollTop - container.clientHeight < 150;

    // If user scrolls UP at all, immediately break auto-scroll
    if (scrollDelta < 0) {
      userScrolledUp.current = true;
      savedScrollTop.current = currentScrollTop;
      return; // Early return prevents isNearBottom from resetting userScrolledUp
    }

    // Save scroll position only when it's meaningful:
    // - if user is controlling scroll (scrolled up), save their position
    // - if they're near bottom, saving bottom is OK too
    if (userScrolledUp.current || isNearBottom) {
      savedScrollTop.current = currentScrollTop;
    }

    // Only reset userScrolledUp when we are near bottom.
    // Do NOT set it to true here — manual mode is entered only on explicit upward scroll.
    if (isNearBottom) {
      userScrolledUp.current = false;
    }
  }, []);

  // Scroll to bottom only when:
  // 1. New messages are added (message count increases)
  // 2. Streaming completes (last message stops streaming)
  // 3. AND user hasn't scrolled up
  const prevMessageCountRef = useRef(0);
  const wasStreamingRef = useRef(false);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const lastMessage = messages[messages.length - 1];
    const isStreaming = lastMessage?.isStreaming ?? false;

    const messageCountChanged = messages.length !== prevMessageCountRef.current;
    const streamingJustEnded = wasStreamingRef.current && !isStreaming;

    // Update tracking refs
    prevMessageCountRef.current = messages.length;
    wasStreamingRef.current = isStreaming;

    if (!container) return;

    // HARD RULE: If user scrolled up, never auto-scroll (streaming or not)
    // User must scroll back to bottom to resume auto-scroll
    if (userScrolledUp.current) return;

    // While streaming: follow bottom instantly (no smooth to avoid jitter)
    if (isStreaming) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    // Non-streaming: smooth scroll on new message or when streaming ends
    if (messageCountChanged || streamingJustEnded) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  // Restore scroll position on reopen OR bootstrap to bottom on fresh load
  useEffect(() => {
    if (!isOpen) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    // If we have a saved position AND this is a reopen (not just message growth), restore it
    if (shouldRestoreOnReopen.current && savedScrollTop.current !== null) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = messagesContainerRef.current;
          if (!el) return;

          const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
          el.scrollTop = Math.min(savedScrollTop.current!, maxScroll);

          // Mark bootstrap as done so we never auto-scroll-to-bottom on this reopen
          didBootstrapScroll.current = true;

          // Clear flags after restoring
          savedScrollTop.current = null;
          shouldRestoreOnReopen.current = false;
        });
      });
      return;
    }

    // Fresh load: only bootstrap once we actually have messages to scroll to
    if (!messages.length) return;
    if (didBootstrapScroll.current) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    });

    didBootstrapScroll.current = true;
    userScrolledUp.current = false;
  }, [isOpen, messages.length]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle pending question from external component (e.g., "Ask Filmply")
  useEffect(() => {
    if (isOpen && pendingQuestion && !isLoading && session?.access_token) {
      setShowHistory(false);
      setInputValue(pendingQuestion);
      clearPendingQuestion();
      // Auto-send the question after a brief delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 200);
    }
  }, [isOpen, pendingQuestion, isLoading, session?.access_token, clearPendingQuestion]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !session?.access_token) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setError(null);

    // Reset scroll flag - user sending message means they want to see response
    userScrolledUp.current = false;

    // Add user message to UI
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    // Add placeholder for streaming response
    setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);
    setIsLoading(true);

    // Force scroll to bottom after DOM updates
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });

    try {
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.code === 'RATE_LIMITED') {
          setError('You\'ve reached your monthly message limit. Upgrade to Pro for unlimited chat!');
        } else {
          setError(data.error || 'Failed to send message');
        }
        // Remove the placeholder and user message
        setMessages(prev => prev.slice(0, -2));
        setIsLoading(false);
        return;
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let messageId: string | undefined;
      let buffer = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events properly - split by double newline
          const events = buffer.split('\n\n');
          buffer = events.pop() || ''; // Keep incomplete event in buffer

          for (const event of events) {
            if (!event.trim()) continue;

            const lines = event.split('\n');
            let eventType = '';
            let eventData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }

            if (!eventType || !eventData) continue;

            try {
              const data = JSON.parse(eventData);

              if (eventType === 'session') {
                if (data.session_id) {
                  setLoadedSessionId(data.session_id);
                  setHistoryLoaded(true);
                  setSessionId(data.session_id);
                }
                if (typeof data.remaining === 'number') {
                  setStatus(prev => prev ? { ...prev, remaining: data.remaining, isPro: data.isPro } : null);
                }
              } else if (eventType === 'chunk') {
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastIdx = newMessages.length - 1;
                  const lastMsg = newMessages[lastIdx];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    // Immutable update: create new object instead of mutating
                    newMessages[lastIdx] = { ...lastMsg, content: lastMsg.content + data.text };
                  }
                  return newMessages;
                });
              } else if (eventType === 'replace') {
                if (typeof data.text === 'string') {
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastIdx = newMessages.length - 1;
                    const lastMsg = newMessages[lastIdx];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      newMessages[lastIdx] = { ...lastMsg, content: data.text };
                    }
                    return newMessages;
                  });
                }
              } else if (eventType === 'done') {
                messageId = data.message_id;
                if (typeof data.remaining === 'number') {
                  setStatus(prev => prev ? { ...prev, remaining: data.remaining } : null);
                }
                if (typeof data.final_text === 'string' && data.final_text.trim().length > 0) {
                  setMessages(prev => {
                    const newMessages = [...prev];
                    const lastIdx = newMessages.length - 1;
                    const lastMsg = newMessages[lastIdx];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      newMessages[lastIdx] = { ...lastMsg, content: data.final_text };
                    }
                    return newMessages;
                  });
                }
              } else if (eventType === 'error') {
                if (data.code === 'RATE_LIMITED') {
                  setError('You\'ve reached your monthly message limit. Upgrade to Pro for unlimited chat!');
                } else {
                  setError(data.error || 'Something went wrong');
                }
              }
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }

      // Finalize the streaming message
      setMessages(prev => {
        const newMessages = [...prev];
        const lastIdx = newMessages.length - 1;
        const lastMsg = newMessages[lastIdx];
        if (lastMsg && lastMsg.role === 'assistant') {
          // Immutable update: create new object instead of mutating
          newMessages[lastIdx] = { ...lastMsg, isStreaming: false, id: messageId, feedback: null };
        }
        return newMessages;
      });
    } catch (err) {
      console.error('Chat error:', err);
      setError('Failed to send message. Please try again.');
      setMessages(prev => prev.slice(0, -2));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (messageId: string, rating: 'positive' | 'negative') => {
    if (!session?.access_token || !messageId) return;

    // Update UI immediately
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId ? { ...msg, feedback: rating } : msg
      )
    );

    try {
      await fetch(`${API_BASE_URL}/chat/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          message_id: messageId,
          rating,
        }),
      });
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Format message content as safe React nodes (no dangerouslySetInnerHTML)
  const formatContent = (content: string): React.ReactNode => {
    const parts = content.split(/(\*\*.+?\*\*)/g);
    const elements: React.ReactNode[] = [];
    let key = 0;
    for (const part of parts) {
      if (part.startsWith('**') && part.endsWith('**')) {
        elements.push(<strong key={key++} className="font-semibold text-teal-400">{part.slice(2, -2)}</strong>);
      } else {
        const lines = part.split('\n');
        for (let j = 0; j < lines.length; j++) {
          if (j > 0) elements.push(<br key={key++} />);
          if (lines[j]) elements.push(lines[j]);
        }
      }
    }
    return <>{elements}</>;
  };

  // Derived: true only for uncached session hydration (no skeleton on cached reopen)
  const hasCachedSession = !!sessionId && Object.prototype.hasOwnProperty.call(sessionMessagesCacheRef.current, sessionId);
  const isHydratingHistory = isOpen && !!sessionId && !isLoading && !hasCachedSession && !(historyLoaded && loadedSessionId === sessionId);

  // Don't render for non-logged-in users
  if (!user) {
    return null;
  }

  return (
    <>
      {/* Floating chat button - teal gradient with popcorn animation */}
      <button
        onClick={openChat}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 hover:shadow-2xl hover:shadow-teal-500/40 shadow-xl flex items-center justify-center transition-all duration-300 ease-out md:bottom-6 bottom-20 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100 hover:scale-110'
        }`}
        aria-label="Open chat"
      >
        <Popcorn
          className={`w-7 h-7 text-white transition-transform duration-300 ${
            isHovered && !isOpen ? 'rotate-[15deg]' : ''
          }`}
        />
        {/* Popcorn kernel dots - only show when hovered and closed, pointer-events-none to not block clicks */}
        {isHovered && !isOpen && (
          <>
            <span
              className="absolute w-2 h-2 rounded-full bg-amber-200 opacity-0 animate-[popcorn-pop-1_0.4s_ease-out_forwards]"
              style={{ top: '50%', left: '50%' }}
            />
            <span
              className="absolute w-1.5 h-1.5 rounded-full bg-yellow-100 opacity-0 animate-[popcorn-pop-2_0.4s_ease-out_0.05s_forwards]"
              style={{ top: '50%', left: '50%' }}
            />
            <span
              className="absolute w-2 h-2 rounded-full bg-amber-100 opacity-0 animate-[popcorn-pop-3_0.4s_ease-out_0.1s_forwards]"
              style={{ top: '50%', left: '50%' }}
            />
            <span
              className="absolute w-1.5 h-1.5 rounded-full bg-yellow-200 opacity-0 animate-[popcorn-pop-4_0.4s_ease-out_0.15s_forwards]"
              style={{ top: '50%', left: '50%' }}
            />
          </>
        )}
      </button>

      {/* Chat window - only render when open */}
      {isOpen && (
      <div
        ref={chatWindowRef}
        className="fixed bottom-24 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] h-[540px] max-h-[calc(100vh-48px)] rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out bg-[#0F1419] border border-[#14b8a6]/20 flex flex-col overscroll-contain"
        style={{ fontFamily: '"Nunito", "Segoe UI", system-ui, sans-serif' }}
      >
        {/* Header - teal gradient */}
        <div className="h-[64px] flex items-center justify-between px-4 bg-gradient-to-r from-teal-600 via-teal-500 to-cyan-500">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-[17px] drop-shadow-sm tracking-tight">Filmply</h3>
              <p className="text-[13px] text-white/80 font-normal">Your film companion</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status && !status.isPro && (
              <span className="text-xs text-white/90 bg-white/20 px-3 py-1 rounded-full font-medium">
                {status.remaining} chats left
              </span>
            )}
            <button
              onClick={() => {
                setShowHistory((prev) => {
                  const next = !prev;
                  if (next) {
                    fetchSessions();
                  }
                  return next;
                });
              }}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              aria-label={showHistory ? 'Back to chat' : 'Open chat history'}
              title={showHistory ? 'Back to chat' : 'History'}
            >
              {showHistory ? (
                <Sparkles className="w-5 h-5 text-white" />
              ) : (
                <Clock className="w-5 h-5 text-white" />
              )}
            </button>
            <button
              onClick={closeChat}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* History panel */}
        {showHistory && (
          <div ref={historyContainerRef} className="flex-1 overflow-y-auto overscroll-contain">
            {sessionsLoading && !sessionsHydrated ? (
              <div className="p-5 space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-[#1A1F26] rounded-xl p-4 border border-white/5 animate-pulse">
                    <div className="h-4 bg-white/10 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-white/10 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : sessionsError && !sessionsHydrated ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <Clock className="w-10 h-10 text-white/20 mb-3" />
                <p className="text-rose-300 text-sm">{sessionsError}</p>
                <button
                  onClick={fetchSessions}
                  className="mt-3 text-xs text-teal-300 hover:text-teal-200 underline"
                >
                  Retry
                </button>
              </div>
            ) : sessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <Clock className="w-10 h-10 text-white/20 mb-3" />
                <p className="text-white/50 text-sm">No past conversations</p>
                <p className="text-white/30 text-xs mt-1">Your chat history will appear here</p>
              </div>
            ) : (
              <div className="p-3 space-y-1">
                {sessions.map(s => {
                  const isEditing = editingSessionId === s.id;
                  return (
                    <div
                      key={s.id}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${
                        s.id === sessionId
                          ? 'bg-teal-500/20 border border-teal-500/30'
                          : 'bg-[#1A1F26] border border-white/5 hover:border-teal-500/20 hover:bg-[#1E2530]'
                      } ${renameSaving && isEditing ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      {isEditing ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <input
                              ref={editInputRef}
                              type="text"
                              value={editingTitle}
                              onChange={e => setEditingTitle(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); renameSession(s.id, editingTitle); }
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              maxLength={80}
                              className="flex-1 bg-[#0F1419] border border-teal-500/40 rounded-lg px-2 py-1 text-sm text-white focus:outline-none focus:border-teal-400"
                              aria-label="Rename chat session"
                              disabled={renameSaving}
                            />
                            <button
                              onClick={() => renameSession(s.id, editingTitle)}
                              className="p-1 rounded hover:bg-teal-500/20 text-teal-400"
                              aria-label="Save title"
                              disabled={renameSaving}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1 rounded hover:bg-white/10 text-white/40"
                              aria-label="Cancel rename"
                              disabled={renameSaving}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {renameError && (
                            <p className="text-xs text-rose-400 mt-1">{renameError}</p>
                          )}
                        </div>
                      ) : (
                        <div
                          role="button"
                          tabIndex={0}
                          className="w-full text-left cursor-pointer"
                          onClick={() => {
                            const cached = Object.prototype.hasOwnProperty.call(sessionMessagesCacheRef.current, s.id);
                            setSessionId(s.id);
                            if (cached) {
                              setMessages(sessionMessagesCacheRef.current[s.id]);
                              setLoadedSessionId(s.id);
                              setHistoryLoaded(true);
                            } else {
                              setHistoryLoaded(false);
                              setMessages([]);
                            }
                            didBootstrapScroll.current = false;
                            userScrolledUp.current = false;
                            setShowHistory(false);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              const cached = Object.prototype.hasOwnProperty.call(sessionMessagesCacheRef.current, s.id);
                              setSessionId(s.id);
                              if (cached) {
                                setMessages(sessionMessagesCacheRef.current[s.id]);
                                setLoadedSessionId(s.id);
                                setHistoryLoaded(true);
                              } else {
                                setHistoryLoaded(false);
                                setMessages([]);
                              }
                              didBootstrapScroll.current = false;
                              userScrolledUp.current = false;
                              setShowHistory(false);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-white font-medium truncate mr-2">
                              {s.title || 'Untitled chat'}
                            </span>
                            <span className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); startEditing(s); }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    if (e.key === ' ') e.preventDefault();
                                  }
                                }}
                                className="p-0.5 rounded hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors"
                                aria-label="Rename chat"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              <span className="text-xs text-white/40">
                                {getRelativeTime(s.last_message_at)}
                              </span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/40">
                              {s.message_count} message{s.message_count !== 1 ? 's' : ''}
                            </span>
                            {!s.is_active && (
                              <span className="text-xs text-white/30 italic">ended</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Messages area */}
        {!showHistory && (
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overscroll-contain"
        >
          {/* Hydration skeleton — shown while loading history */}
          {isHydratingHistory && (
            <div className="p-5 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className={`flex ${i % 2 === 1 ? 'justify-end' : 'justify-start'}`}>
                  <div className={`rounded-2xl px-4 py-3 animate-pulse ${i % 2 === 1 ? 'bg-teal-500/20 w-[60%] rounded-br-[4px]' : 'bg-[#1A1F26] border border-white/5 w-[75%] rounded-bl-[4px]'}`}>
                    <div className="h-3 bg-white/10 rounded w-full mb-2" />
                    <div className="h-3 bg-white/10 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Intro message (show if no messages and not hydrating) */}
          {!isHydratingHistory && messages.length === 0 && intro && (
            <div className="p-5">
              <div className="bg-[#1A1F26] rounded-2xl p-5 border border-white/5 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-md">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold text-teal-400 text-[15px]">Hey there!</span>
                </div>
                <div className="text-white/90 text-[15px] leading-relaxed">
                  {formatContent(intro)}
                </div>
              </div>
            </div>
          )}

          {/* Chat messages (hidden during hydration) */}
          {!isHydratingHistory && (
          <div className="p-5 space-y-4">
          {messages.map((msg, idx) => {
            // Don't show empty streaming placeholder - we show "Thinking" instead
            if (msg.isStreaming && msg.content === '') return null;

            return (
            <div
              key={msg.id || idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-br-[4px] shadow-md'
                    : 'bg-[#1A1F26] text-white/90 rounded-bl-[4px] border border-white/5'
                }`}
              >
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-teal-400 text-xs font-bold">Filmply</span>
                  </div>
                )}
                <div className="text-sm leading-relaxed">
                  {formatContent(msg.content)}
                </div>
                {msg.isStreaming && msg.content && (
                  <span className="inline-block w-2 h-4 bg-teal-400 ml-1 animate-pulse rounded-sm" />
                )}

                {/* Feedback buttons for assistant messages */}
                {msg.role === 'assistant' && msg.id && !msg.isStreaming && (
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t border-white/10">
                    <span className="text-xs text-white/40 mr-1">Helpful?</span>
                    <button
                      onClick={() => handleFeedback(msg.id!, 'positive')}
                      className={`p-1.5 rounded-lg transition-all ${
                        msg.feedback === 'positive'
                          ? 'text-green-400 bg-green-500/20 scale-110'
                          : 'text-white/40 hover:text-green-400 hover:bg-green-500/10'
                      }`}
                      aria-label="Good response"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleFeedback(msg.id!, 'negative')}
                      className={`p-1.5 rounded-lg transition-all ${
                        msg.feedback === 'negative'
                          ? 'text-rose-400 bg-rose-500/20 scale-110'
                          : 'text-white/40 hover:text-rose-400 hover:bg-rose-500/10'
                      }`}
                      aria-label="Bad response"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            );
          })}

          {/* Loading indicator */}
          {isLoading && messages[messages.length - 1]?.content === '' && (
            <div className="flex justify-start">
              <div className="bg-[#1A1F26] border border-white/5 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center animate-pulse">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-teal-400 text-sm font-medium">
                    Thinking
                    <span className="inline-flex ml-1">
                      <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-3 bg-rose-500/10 border border-rose-500/20 rounded-2xl p-4">
              <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-rose-400 font-medium">{error}</p>
                {error.includes('Pro') && (
                  <a
                    href="/pricing"
                    className="text-teal-400 hover:underline mt-2 inline-block font-semibold"
                  >
                    Upgrade to Pro →
                  </a>
                )}
              </div>
            </div>
          )}
          </div>
          )}
        </div>
        )}

        {/* Input area */}
        {!showHistory && (
        <div className="shrink-0 p-4 border-t border-white/10 bg-[#0F1419]">
          <div className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What should I watch tonight?"
              className="flex-1 w-full bg-[#1A1F26] border border-[#14b8a6]/30 text-white text-[15px] placeholder:text-white/40 focus-visible:ring-teal-500 focus-visible:border-teal-500 rounded-full py-3 px-4 resize-none focus:outline-none transition-all min-h-[48px] max-h-[120px]"
              rows={1}
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="shrink-0 w-11 h-11 rounded-full bg-[#14b8a6] hover:bg-[#0d9488] text-white shadow-lg disabled:opacity-60 flex items-center justify-center transition-all"
              aria-label="Send message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>

          {/* Remaining messages counter for free users */}
          {status && !status.isPro && (
            <p className="text-xs text-white/50 mt-3 text-center">
              {status.remaining > 0
                ? `${status.remaining} of ${status.limit} free messages this month`
                : 'No free messages remaining'}
              {' · '}
              <a href="/pricing" className="text-teal-400 hover:underline font-semibold">
                Go unlimited
              </a>
            </p>
          )}
        </div>
        )}
      </div>
      )}
    </>
  );
}
